import React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import AnimatedModal from '../../components/ui/AnimatedModal';
import { getCandidatesTableLayout } from './candidatesTableLayout';
import { buildAssessmentModuleColumnChoices } from './assessmentModuleColumns';
import { META_COLUMN_DEFAULT_ORDER, isTranscriptsColumnAvailable } from './CandidatesTable';
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ViewColumnsIcon,
  XMarkIcon,
  Bars3Icon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const COLUMN_GROUP_ORDER = ['Scoring', 'Candidate profile', 'Contact info', 'Application', 'Operations'];

const SORT_OPTIONS = [
  { id: 'overallScore', name: 'Overall Score' },
  { id: 'resumeScore', name: 'Resume Score' },
  { id: 'assessmentScore', name: 'Assessment Score' },
  { id: 'interviewScore', name: 'Interview Score' },
  { id: 'appliedDate', name: 'Applied Date' },
  { id: 'completedAt', name: 'Completed Date' },
];

const COLUMN_GROUP_LABELS = {
  Scoring: 'Scoring',
  'Candidate profile': 'Candidate profile',
  'Contact info': 'Contact info',
  Application: 'Application details',
  Operations: 'Operations',
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const resolveVisibility = (column, overrides = {}) =>
  hasOwn(overrides, column.id) ? overrides[column.id] === true : column.defaultVisible === true;

const groupColumns = (columns) => {
  const grouped = columns.reduce((acc, column) => {
    const group = column.group || 'Scoring';
    if (!acc[group]) acc[group] = [];
    acc[group].push(column);
    return acc;
  }, {});

  return COLUMN_GROUP_ORDER.flatMap((group) =>
    grouped[group]
      ? [{ group, columns: grouped[group] }]
      : []
  ).concat(
    Object.keys(grouped)
      .filter((group) => !COLUMN_GROUP_ORDER.includes(group))
      .sort((a, b) => a.localeCompare(b))
      .map((group) => ({ group, columns: grouped[group] }))
  );
};

export function buildCandidateColumnChoices(screeningMeta, tableOptions = {}) {
  const layout = getCandidatesTableLayout(screeningMeta, tableOptions);
  const showStatus = tableOptions?.showStatus === true;
  const showShortlistedDate = tableOptions?.showShortlistedDate === true;
  const showRejectedDate = tableOptions?.showRejectedDate === true;
  const showDocumentsColumn = tableOptions?.showDocumentsColumn === true;
  const assessmentModuleColumns = screeningMeta?.showAssessmentModuleColumns
    ? buildAssessmentModuleColumnChoices(screeningMeta.assessmentModules)
    : [];

  const baseChoices = [
    {
      id: 'status',
      label: 'Status',
      description: 'Show the candidate progress state.',
      group: 'Scoring',
      defaultVisible: showStatus,
      available: showStatus,
    },
    {
      id: 'overallScore',
      label: 'Overall score',
      description: 'Combined score across the active evaluation flow.',
      group: 'Scoring',
      defaultVisible: layout.showOverallScoreColumn,
      available: true,
    },
    {
      id: 'resumeScore',
      label: 'Resume score',
      description: 'Resume parsing / resume scoring result.',
      group: 'Scoring',
      defaultVisible: layout.showResumeScoreColumn,
      available: screeningMeta?.hasResumeScoring !== false,
    },
    {
      id: 'assessmentScore',
      label: 'Assessment score',
      description: 'Online assessment score and breakdown entry point.',
      group: 'Scoring',
      defaultVisible: layout.showAssessmentScoreColumn,
      available: screeningMeta?.includeAssessment !== false,
    },
    ...assessmentModuleColumns,
    {
      id: 'interviewScore',
      label: screeningMeta?.includeWhatsAppAgent ? 'WhatsApp score' : 'Interview score',
      description: screeningMeta?.includeWhatsAppAgent
        ? 'WhatsApp Agent interview score and question-by-question analysis.'
        : 'AI interview score and session analysis entry point.',
      group: 'Scoring',
      defaultVisible: layout.showInterviewScoreColumn,
      available: screeningMeta?.includeInterview !== false,
    },
    {
      id: 'decision',
      label: 'Decision',
      description: 'The recruiter\'s decision on this candidate.',
      group: 'Operations',
      defaultVisible: true,
      available: tableOptions?.isShortlistView === true,
    },
    {
      id: 'notes',
      label: 'Notes',
      description: 'Private notes for this candidate.',
      group: 'Operations',
      defaultVisible:
        tableOptions?.notesDefaultVisible ?? (tableOptions?.isShortlistView === true),
      available: true,
    },
    {
      id: 'shortlistedDate',
      label: 'Selected date',
      description: 'When the candidate was shortlisted.',
      group: 'Operations',
      defaultVisible: showShortlistedDate,
      available: tableOptions?.isShortlistView === true,
    },
    {
      id: 'salaryDetails',
      label: 'Salary & Designation',
      description: 'Designation, joining date, and gross salary entered by Regional Head / ARM.',
      group: 'Operations',
      defaultVisible: tableOptions?.hasArmSalaryDetails === true,
      available: true,
    },
    {
      id: 'rejectedDate',
      label: 'Rejected date',
      description: 'When the candidate was rejected.',
      group: 'Operations',
      defaultVisible: showRejectedDate,
      available: tableOptions?.isRejectedView === true,
    },
    {
      id: 'duplicates',
      label: 'Duplicates',
      description: 'Show potential duplicate candidate records.',
      group: 'Operations',
      defaultVisible: tableOptions?.showDuplicateColumn === true,
      available: true,
    },
    {
      id: 'proctoring',
      label: 'Proctoring',
      description: 'Add the proctoring / risk summary column.',
      group: 'Scoring',
      defaultVisible: layout.showProctoringColumn,
      available: true,
    },
    {
      id: 'appliedDate',
      label: 'Applied',
      description: 'When the candidate applied.',
      group: 'Operations',
      defaultVisible: layout.showAppliedDateColumn,
      available: true,
    },
    {
      id: 'screening',
      label: 'Screening',
      description: 'The screening the candidate belongs to.',
      group: 'Application',
      defaultVisible: layout.showScreeningColumn,
      available: true,
    },
    {
      id: 'companyName',
      label: 'Current company',
      description: 'The candidate’s current employer.',
      group: 'Candidate profile',
      defaultVisible: layout.showCompanyNameColumn,
      available: true,
    },
    {
      id: 'storeId',
      label: 'Store Code',
      description: 'The mapped store code for the candidate.',
      group: 'Candidate profile',
      defaultVisible: tableOptions?.user?.features?.store_list === true,
      available: tableOptions?.user?.features?.store_list === true,
    },
    {
      id: 'storeName',
      label: 'Store Name',
      description: 'The mapped store name.',
      group: 'Candidate profile',
      defaultVisible: layout.showStoreNameColumn,
      available: tableOptions?.user?.features?.store_list === true,
    },
    {
      id: 'storeRegion',
      label: 'Store Region',
      description: 'The region of the mapped store.',
      group: 'Candidate profile',
      defaultVisible: layout.showStoreRegionColumn,
      available: tableOptions?.user?.features?.store_list === true,
    },
    {
      id: 'storeCity',
      label: 'Store City',
      description: 'The city of the mapped store.',
      group: 'Candidate profile',
      defaultVisible: layout.showStoreCityColumn,
      available: tableOptions?.user?.features?.store_list === true,
    },
    {
      id: 'storeState',
      label: 'Store State',
      description: 'The state of the mapped store.',
      group: 'Candidate profile',
      defaultVisible: layout.showStoreStateColumn,
      available: tableOptions?.user?.features?.store_list === true,
    },
    {
      id: 'storeCategory',
      label: 'Store Category',
      description: 'The category of the mapped store.',
      group: 'Candidate profile',
      defaultVisible: layout.showStoreCategoryColumn,
      available: tableOptions?.user?.features?.store_list === true,
    },
    {
      id: 'armName',
      label: 'ARM Name',
      description: 'Area Regional Manager Name.',
      group: 'Candidate profile',
      defaultVisible: layout.showArmNameColumn,
      available: tableOptions?.user?.features?.store_list === true,
    },
    {
      id: 'armEmail',
      label: 'ARM Email',
      description: 'Area Regional Manager Email.',
      group: 'Candidate profile',
      defaultVisible: layout.showArmEmailColumn,
      available: tableOptions?.user?.features?.store_list === true,
    },
    {
      id: 'cityCode',
      label: 'City Code',
      description: 'The mapped city code for the candidate.',
      group: 'Candidate profile',
      defaultVisible: tableOptions?.user?.features?.city_list === true,
      available: tableOptions?.user?.features?.city_list === true,
    },
    {
      id: 'cityCity',
      label: 'City Name',
      description: 'The mapped city name.',
      group: 'Candidate profile',
      defaultVisible: layout.showCityCityColumn,
      available: tableOptions?.user?.features?.city_list === true,
    },
    {
      id: 'cityState',
      label: 'City State',
      description: 'The mapped city state.',
      group: 'Candidate profile',
      defaultVisible: layout.showCityStateColumn,
      available: tableOptions?.user?.features?.city_list === true,
    },
    {
      id: 'cityRegion',
      label: 'City Region',
      description: 'The mapped city region.',
      group: 'Candidate profile',
      defaultVisible: layout.showCityRegionColumn,
      available: tableOptions?.user?.features?.city_list === true,
    },
    {
      id: 'currentCtc',
      label: 'Current CTC',
      description: 'Current compensation.',
      group: 'Candidate profile',
      defaultVisible: layout.showCurrentCtcColumn,
      available: true,
    },
    {
      id: 'noticePeriod',
      label: 'Notice period',
      description: 'Candidate notice period.',
      group: 'Candidate profile',
      defaultVisible: layout.showNoticePeriodColumn,
      available: true,
    },
    {
      id: 'experience',
      label: 'Experience (Band)',
      description: 'Relevant years of experience.',
      group: 'Candidate profile',
      defaultVisible:
        layout.showExperienceColumn ||
        layout.showResumeAssessmentExperienceColumn ||
        layout.showResumeInterviewExperienceColumn,
      available: true,
    },
    {
      id: 'experienceYears',
      label: 'Experience (Years)',
      description: 'Total years of experience.',
      group: 'Candidate profile',
      defaultVisible: layout.showExperienceYearsColumn,
      available: true,
    },
    {
      id: 'age',
      label: 'Age',
      description: 'Candidate age.',
      group: 'Candidate profile',
      defaultVisible: layout.showAgeColumn,
      available: true,
    },
    {
      id: 'gender',
      label: 'Gender',
      description: 'Candidate gender.',
      group: 'Candidate profile',
      defaultVisible: layout.showGenderColumn,
      available: true,
    },
    {
      id: 'latestEducation',
      label: 'Latest education',
      description: 'Candidate education summary.',
      group: 'Candidate profile',
      defaultVisible: layout.showLatestEducationColumn,
      available: true,
    },
    {
      id: 'designation',
      label: 'Designation',
      description: 'Current role or title listed on the candidate profile.',
      group: 'Candidate profile',
      defaultVisible: layout.showDesignationColumn,
      available: true,
    },
    {
      id: 'location',
      label: 'Location',
      description: 'Primary work or preferred location.',
      group: 'Candidate profile',
      defaultVisible: layout.showLocationColumn,
      available: true,
    },
    {
      id: 'industry',
      label: 'Industry',
      description: 'Industry tags derived from the resume or application.',
      group: 'Candidate profile',
      defaultVisible: layout.showIndustryColumn,
      available: true,
    },
    {
      id: 'skills',
      label: 'Skills',
      description: 'Primary skills listed for the candidate.',
      group: 'Candidate profile',
      defaultVisible: layout.showSkillsColumn,
      available: true,
    },
    {
      id: 'candidateDetails',
      label: 'Candidate details',
      description: 'Open the compact candidate details panel.',
      group: 'Operations',
      defaultVisible: layout.showCandidateDetailsColumn,
      available: true,
    },
    {
      id: 'completedAt',
      label: 'Completed at',
      description: 'Show the completion timestamp.',
      group: 'Operations',
      defaultVisible: layout.showCompletedAtColumn,
      available: true,
    },
    {
      id: 'qualifyingCall',
      label: 'Qualifying call',
      description: 'Call and WhatsApp progress, and the conversation transcript.',
      group: 'Operations',
      defaultVisible: layout.showQualifyingCallColumn,
      available: screeningMeta?.includeQualifyingAgent === true,
    },
    {
      id: 'applicationForm',
      label: 'Application form',
      description: 'Whether the candidate submitted the pre-interview application form; opens it and the PDF.',
      group: 'Operations',
      defaultVisible: screeningMeta?.includeApplicationForm === true,
      available: screeningMeta?.includeApplicationForm === true,
    },
    {
      id: 'transcriptsAvailable',
      label: 'Transcripts available',
      description: 'Whether the candidate ticked "Are transcripts available for each semester/year?" on the resume upload step.',
      group: 'Operations',
      defaultVisible: isTranscriptsColumnAvailable(tableOptions?.user, screeningMeta),
      available: isTranscriptsColumnAvailable(tableOptions?.user, screeningMeta),
    },
    {
      id: 'documents',
      label: 'Documents',
      description: 'Document collection status and quick viewer for this candidate.',
      group: 'Operations',
      defaultVisible: showDocumentsColumn,
      available: true,
    },
    {
      id: 'email',
      label: 'Email',
      description: 'Primary email address for the candidate.',
      group: 'Contact info',
      defaultVisible: layout.showEmailColumn,
      available: true,
    },
    {
      id: 'phone',
      label: 'Phone number',
      description: 'Candidate phone or mobile number.',
      group: 'Contact info',
      defaultVisible: layout.showPhoneColumn,
      available: true,
    },
    {
      id: 'social',
      label: 'Social profiles',
      description: 'LinkedIn, GitHub, portfolio, and other public links.',
      group: 'Contact info',
      defaultVisible: layout.showSocialColumn,
      available: true,
    },
  ];

  // Standard registration field IDs that are already represented by built-in table columns:
  //   'name'  → the Candidate Name column (always shown as the first fixed column)
  //   'email' → the 'email' (Email) built-in column
  //   'phone' → the 'phone' (Phone number) built-in column
  // We exclude them here so they don't show up as duplicate columns in the picker.
  const STANDARD_FIELD_IDS = new Set(['name', 'email', 'phone', 'mobile']);

  const explicitOrgOrder = Array.isArray(tableOptions?.orgDefaultColumnOrder) && tableOptions.orgDefaultColumnOrder.length > 0
    ? tableOptions.orgDefaultColumnOrder
    : (Array.isArray(tableOptions?.user?.ui?.defaultCandidateColumnOrder) && tableOptions.user.ui.defaultCandidateColumnOrder.length > 0
      ? tableOptions.user.ui.defaultCandidateColumnOrder
      : null);

  const customFieldsColumns = (tableOptions?.user?.registrationFields || [])
    .filter((f) => !STANDARD_FIELD_IDS.has(f.fieldId))
    .map((f) => {
      const colId = f.fieldId.startsWith('custom_') ? f.fieldId : `custom_${f.fieldId}`;
      // Any field that is actively configured in registrationFields is default-visible.
      // The saved column order governs ordering, not whether a field should appear by default.
      // This ensures newly added fields (e.g. DOB added after a column order was saved)
      // show as Default in the picker immediately without requiring a manual reset.
      return {
        id: colId,
        label: f.label || f.fieldId,
        description: 'Custom field collected during registration',
        group: 'Custom Fields',
        defaultVisible: true,
        available: true,
      };
    });


  const choices = baseChoices.concat(customFieldsColumns).filter((column) => column.available !== false);

  const orgOrder = Array.isArray(tableOptions?.orgDefaultColumnOrder) && tableOptions.orgDefaultColumnOrder.length > 0
    ? [...tableOptions.orgDefaultColumnOrder]
    : (Array.isArray(tableOptions?.user?.ui?.defaultCandidateColumnOrder) && tableOptions.user.ui.defaultCandidateColumnOrder.length > 0
      ? [...tableOptions.user.ui.defaultCandidateColumnOrder]
      : [...META_COLUMN_DEFAULT_ORDER]);

  // Ensure new store columns are forcefully injected after storeRegion so they group together
  const storeRegionIdx = orgOrder.indexOf('storeRegion');
  if (storeRegionIdx !== -1) {
    const storeColsToInject = ['storeCity', 'storeState', 'storeCategory', 'armName', 'armEmail'].filter(c => !orgOrder.includes(c));
    storeColsToInject.forEach((id, idx) => {
        orgOrder.splice(storeRegionIdx + 1 + idx, 0, id);
    });
  }

  // Ensure city columns are injected if they aren't present
  const cityIdx = orgOrder.indexOf('cityCode');
  if (cityIdx === -1) {
    const emailIdx = orgOrder.indexOf('email');
    if (emailIdx !== -1) {
      orgOrder.splice(emailIdx, 0, 'cityCode', 'cityCity', 'cityState', 'cityRegion');
    }
  }

  // Extend META_COLUMN_DEFAULT_ORDER with custom fields for a sensible fallback placement
  const extendedFallbackOrder = [...META_COLUMN_DEFAULT_ORDER];
  const experienceIdx = extendedFallbackOrder.indexOf('experience');
  const insertIdx = experienceIdx !== -1 ? experienceIdx + 1 : extendedFallbackOrder.length;
  extendedFallbackOrder.splice(insertIdx, 0, ...customFieldsColumns.map(c => c.id));

  // Sort choices by orgOrder to ensure the modal's default order matches the table's default org order
  choices.sort((a, b) => {
    let indexA = orgOrder.indexOf(a.id);
    let indexB = orgOrder.indexOf(b.id);
    
    // If a column is missing from the explicit org order, we append it at the end
    // sorted according to the extended default order.
    if (indexA === -1) {
      const fallbackA = extendedFallbackOrder.indexOf(a.id);
      indexA = 1000 + (fallbackA === -1 ? 999 : fallbackA);
    }
    if (indexB === -1) {
      const fallbackB = extendedFallbackOrder.indexOf(b.id);
      indexB = 1000 + (fallbackB === -1 ? 999 : fallbackB);
    }
    
    return indexA - indexB;
  });

  return choices;
}

export default function CandidateColumnsModal({
  isOpen,
  onClose,
  columns,
  value,
  columnOrder: savedColumnOrder,
  sortColumn = 'overallScore',
  sortDirection = 'desc',
  onApply,
  tabContext = 'ui', // 'ui', 'shortlist', 'rejected'
  title = 'Customize columns',
  subtitle = 'Choose the columns you want to see in the candidate table.',
}) {
  const [search, setSearch] = React.useState('');
  const [draft, setDraft] = React.useState({});
  const [draftOrder, setDraftOrder] = React.useState([]);
  const [draftSortColumn, setDraftSortColumn] = React.useState(sortColumn);
  const [draftSortDirection, setDraftSortDirection] = React.useState(sortDirection);

  React.useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setDraft(value || {});
    setDraftOrder(Array.isArray(savedColumnOrder) && savedColumnOrder.length > 0 ? savedColumnOrder : []);
    setDraftSortColumn(sortColumn || 'overallScore');
    setDraftSortDirection(sortDirection || 'desc');
  }, [isOpen, value, savedColumnOrder, sortColumn, sortDirection]);

  const resolvedColumns = React.useMemo(
    () =>
      (columns || []).map((column) => {
        const isCustom = column.id.startsWith('custom_');
        let isVis = column.defaultVisible;

        if (draft[column.id] !== undefined) {
          isVis = draft[column.id];
        } else if (isCustom && draftOrder.includes(column.id)) {
          isVis = true;
        }

        return {
          ...column,
          visible: isVis,
        };
      }),
    [columns, draft, draftOrder]
  );

  const selectedColumns = React.useMemo(() => {
    const visible = resolvedColumns.filter((column) => column.visible);
    if (draftOrder.length === 0) return visible;
    const byId = new Map(visible.map((c) => [c.id, c]));
    const ordered = [];
    draftOrder.forEach((id) => {
      const col = byId.get(id);
      if (col) {
        ordered.push(col);
        byId.delete(id);
      }
    });
    byId.forEach((col) => ordered.push(col));
    return ordered;
  }, [resolvedColumns, draftOrder]);

  const filteredColumns = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return resolvedColumns;
    return resolvedColumns.filter((column) => {
      return [column.label, column.description, column.group].filter(Boolean).some((text) =>
        String(text).toLowerCase().includes(q)
      );
    });
  }, [resolvedColumns, search]);

  const groupedColumns = React.useMemo(() => groupColumns(filteredColumns), [filteredColumns]);

  const toggleColumn = (columnId) => {
    setDraft((prev) => {
      const next = { ...prev };
      const column = resolvedColumns.find((item) => item.id === columnId);
      const current = column ? column.visible : false;
      next[columnId] = !current;
      return next;
    });
  };

  const setAllColumns = (nextVisible) => {
    const next = {};
    (columns || []).forEach((column) => {
      next[column.id] = nextVisible;
    });
    setDraft(next);
  };


  const applyChanges = () => {
    if (typeof onApply === 'function') {
      const order = selectedColumns.map((c) => c.id);
      onApply(draft, order, draftSortColumn, draftSortDirection);
    }
    onClose?.();
  };

  // ── Drag & drop ──
  const dragItem = React.useRef(null);
  const dragOverItem = React.useRef(null);
  const [draggingId, setDraggingId] = React.useState(null);

  const handleDragStart = (e, index, columnId) => {
    dragItem.current = index;
    // Defer state update to prevent the browser from cancelling the drag
    setTimeout(() => {
      setDraggingId(columnId);
    }, 0);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    if (dragItem.current == null || dragOverItem.current == null || dragItem.current === dragOverItem.current) {
      setDraggingId(null);
      return;
    }
    const items = selectedColumns.map((c) => c.id);
    const [removed] = items.splice(dragItem.current, 1);
    items.splice(dragOverItem.current, 0, removed);
    setDraftOrder(items);
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingId(null);
  };

  const activeSortOptions = React.useMemo(() => {
    const opts = [...SORT_OPTIONS];
    if (tabContext === 'shortlist') {
      opts.push({ id: 'shortlistedDate', name: 'Selected Date' });
    } else if (tabContext === 'rejected') {
      opts.push({ id: 'rejectedDate', name: 'Rejected Date' });
    }
    return opts;
  }, [tabContext]);

  const visibleCount = selectedColumns.length;
  const totalCount = (columns || []).length;

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      backdropClassName="bg-black/40"
      contentClassName="w-full max-w-4xl overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl"
      ariaLabelledBy="candidate-columns-modal-title"
    >
      <div className="flex max-h-[82vh] flex-col">
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 bg-white px-6 py-4 text-slate-900">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600 ring-1 ring-slate-200">
              <ViewColumnsIcon className="h-5 w-5" />
            </div>
            <div>
              <h2 id="candidate-columns-modal-title" className="text-base font-semibold tracking-tight">
                {title}
              </h2>
              <div className="mt-1 inline-flex items-center gap-2 text-xs text-slate-500">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                {visibleCount} of {totalCount} columns visible
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="grid flex-1 gap-0 overflow-hidden lg:grid-cols-[2fr_3fr]">
          <div className="flex min-h-0 flex-col border-r border-slate-100 bg-slate-50/70">
            <div className="border-b border-slate-100 bg-white px-4 py-3">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search columns"
                  className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#007BFF] focus:bg-white focus:ring-4 focus:ring-[#007BFF]/10"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {groupedColumns.map(({ group, columns: groupItems }) => (
                <div key={group} className="mb-4 last:mb-0">
                  <div className="mb-1.5 flex items-center justify-between">
                    <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {COLUMN_GROUP_LABELS[group] || group}
                    </h3>
                    <span className="text-[10px] font-medium text-slate-400">
                      {groupItems.length}
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    {groupItems.map((column) => {
                      const isVisible = column.visible;
                      return (
                        <button
                          key={column.id}
                          type="button"
                          onClick={() => toggleColumn(column.id)}
                          className={`w-full rounded-xl border px-3 py-2 text-left transition ${isVisible
                            ? 'border-[#007BFF]/30 bg-white shadow-sm ring-1 ring-[#007BFF]/10'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                            }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <div
                              className={`flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full border ${isVisible
                                ? 'border-[#007BFF] bg-[#007BFF] text-white'
                                : 'border-slate-300 bg-white text-transparent'
                                }`}
                            >
                              <CheckCircleIcon className="h-3 w-3" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-[13px] font-medium text-slate-900">{column.label}</span>
                                {column.defaultVisible && (
                                  <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-700">
                                    Default
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {groupedColumns.length === 0 && (
                <div className="flex h-full min-h-[180px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white px-6 py-8 text-center">
                  <MagnifyingGlassIcon className="h-8 w-8 text-slate-300" />
                  <p className="mt-2 text-sm font-semibold text-slate-900">No columns found</p>
                  <p className="mt-1 text-xs text-slate-500">Try a different search term.</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex min-h-0 flex-col bg-white">
            <div className="border-b border-slate-100 px-4 py-3 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Selected columns</h3>
                <p className="mt-0.5 text-[11px] text-slate-500">Drag to reorder</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Sort:</span>
                <div className="relative">
                  <Listbox value={draftSortColumn} onChange={setDraftSortColumn}>
                    {({ open }) => (
                      <>
                        <Listbox.Button className="relative w-[130px] cursor-default rounded-lg border border-slate-200 bg-white py-1.5 pl-3 pr-8 text-left text-xs font-medium text-slate-700 shadow-sm outline-none transition-all hover:bg-slate-50 focus:border-[#007BFF] focus:ring-4 focus:ring-[#007BFF]/10">
                          <span className="block truncate">
                            {activeSortOptions.find((opt) => opt.id === draftSortColumn)?.name || 'Overall Score'}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronDownIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>

                        <Transition
                          show={open}
                          as={React.Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute right-0 z-10 mt-1 max-h-60 w-full min-w-[150px] overflow-auto rounded-lg bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {activeSortOptions.map((option) => (
                              <Listbox.Option
                                key={option.id}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-1.5 pl-8 pr-4 transition-colors ${
                                    active ? 'bg-blue-50 text-[#007BFF]' : 'text-slate-700'
                                  }`
                                }
                                value={option.id}
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold text-[#007BFF]' : 'font-medium'}`}>
                                      {option.name}
                                    </span>
                                    {selected ? (
                                      <span
                                        className={`absolute inset-y-0 left-0 flex items-center pl-2.5 ${
                                          active ? 'text-[#007BFF]' : 'text-[#007BFF]'
                                        }`}
                                      >
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </>
                    )}
                  </Listbox>
                </div>
                <button
                  type="button"
                  onClick={() => setDraftSortDirection(draftSortDirection === 'desc' ? 'asc' : 'desc')}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500 shadow-sm transition-all hover:bg-white hover:text-[#007BFF] focus:border-[#007BFF] focus:outline-none focus:ring-4 focus:ring-[#007BFF]/10"
                  title={draftSortDirection === 'desc' ? 'Descending' : 'Ascending'}
                >
                  {draftSortDirection === 'desc' ? (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path></svg>
                  )}
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {selectedColumns.length > 0 ? (
                <div className="space-y-1.5">
                  {selectedColumns.map((column, index) => (
                    <div
                      key={column.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index, column.id)}
                      onDragEnter={(e) => handleDragEnter(e, index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => e.preventDefault()}
                      className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 transition-all select-none ${draggingId === column.id
                        ? 'border-[#007BFF]/40 bg-[#007BFF]/5 shadow-md scale-[1.02]'
                        : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                        }`}
                      style={{ cursor: 'grab' }}
                    >
                      <Bars3Icon className="h-4 w-4 shrink-0 text-slate-400" />
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#007BFF] ring-1 ring-[#007BFF]/10">
                        <span className="text-[10px] font-bold">{index + 1}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="truncate text-[13px] font-medium text-slate-900">
                          {column.label}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleColumn(column.id)}
                        className="rounded-full p-1 text-slate-400 transition hover:bg-white hover:text-rose-600"
                        aria-label={`Remove ${column.label}`}
                        title={`Remove ${column.label}`}
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex h-full min-h-[180px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-center">
                  <ViewColumnsIcon className="h-8 w-8 text-slate-300" />
                  <p className="mt-2 text-sm font-semibold text-slate-900">No columns selected</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Turn on at least one column from the list on the left.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 bg-slate-50 px-4 py-3">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    if (typeof onApply === 'function') {
                      onApply(null, null, null, null);
                    }
                    onClose?.();
                  }}
                  className="text-sm font-medium text-slate-500 transition hover:text-slate-800"
                >
                  Reset to org defaults
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyChanges}
                    className="rounded-lg border border-[#007BFF] bg-white px-4 py-2 text-sm font-semibold text-[#007BFF] shadow-sm transition hover:bg-slate-50"
                  >
                    Apply columns
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
}
